import { addDays } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CourseCategories, CourseTypes } from "../common/constants";
import EasePicker, { AmpPlugin, LockPlugin, PresetPlugin, RangePlugin } from "react-easepick";
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "../api";
import MetricStatBlock from "../components/analytics/MetricStatBlock";
import Card from "../components/cards/Card";
import Breadcrumb from "../components/Breadcrumb";
import Dialog from "../components/dialogs/Dialog";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import Select from "../components/Select";
import StatusBadge from "../components/StatusBadge";
import Table from "../components/Table";
import TextInput from "../components/TextInput";
import TooltipWrapper from "../components/TooltipWrapper";
import { ReactComponent as InfoIcon } from "../assets/images/icons/ic-info.svg";
import { getOrderStatusBadge } from "../utils";
import { Helmet, HelmetProvider } from "react-helmet-async";
import styles from "./OrdersReports.module.css";

const getMinOrdersAnalyticsDate = () => new Date(2025, 6, 11, 9, 18);

const calculatePercentageDifference = (actualTotal, previousTotal) => {
    const actual = Number(actualTotal ?? 0);
    const previous = Number(previousTotal ?? 0);

    if (previous === 0) {
        return actual > 0 ? Infinity : actual < 0 ? -Infinity : 0;
    }

    return ((actual - previous) / Math.abs(previous)) * 100;
};

const formatDateAsRomeDay = (value) => {
    if (!value) {
        return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Rome",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);

    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;

    if (!year || !month || !day) {
        return null;
    }

    return `${year}-${month}-${day}`;
};

const formatCurrencyFromCents = (value) => {
    const cents = Number(value ?? 0);
    return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
    }).format(cents / 100);
};

const formatInteger = (value) => {
    return new Intl.NumberFormat("it-IT", {
        maximumFractionDigits: 0,
    }).format(Number(value ?? 0));
};

const formatPercent = (value) => {
    return `${Number(value ?? 0).toFixed(2)}%`;
};

const formatSignedDaysHours = (value) => {
    const numericValue = Number(value ?? 0);
    if (!Number.isFinite(numericValue)) {
        return "0 ore";
    }

    const totalHours = Math.round(Math.abs(numericValue) * 24);
    if (totalHours === 0) {
        return "0 ore";
    }

    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const timing = numericValue < 0 ? "prima" : "dopo";

    if (days > 0 && hours > 0) {
        return `${days} giorni e ${hours} ore ${timing}`;
    }

    if (days > 0) {
        return `${days} giorni ${timing}`;
    }

    return `${hours} ore ${timing}`;
};

const formatLongDay = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "long" }).format(date);
};

const formatPaymentMethodLabel = (value) => {
    const normalized = String(value || "unknown").trim().toLowerCase();
    if (!normalized || normalized === "unknown") {
        return "Non specificato";
    }

    if (normalized === "customer_balance") {
        return "Bonifico";
    }

    return normalized
        .split("_")
        .map((part) => (part ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : ""))
        .join(" ");
};

const TOOLTIP_METRIC_LABELS = {
    unique_customers: "Clienti creati",
    total_orders: "Ordini creati",
    total_revenue: "Incassi completati",
    succeeded_orders: "Ordini completati",
    partially_paid_orders: "Ordini parzialmente pagati",
    pending_orders: "Ordini in attesa",
    abandoned_orders: "Ordini abbandonati",
    refunded_orders: "Rimborsi completati",
    aov: "Average order value (AOV)",
    pending_to_succeeded_conversion_rate: "Tasso di conversione",
    abandon_rate: "Tasso di abbandono",
    refund_rate: "Tasso di rimborso",
    installment_orders: "Numero ordini a rate",
    installment_adoption_rate: "Tasso adozione rate",
    installments_total_revenue: "Total revenues rate",
    installments_pending_revenue: "Revenues in attesa da rate",
    installments_abandoned_revenue: "Revenues rate abbandonate",
    installments_conversion_rate: "Conversione ordini prima rata",
    installments_full_conversion_rate: "Conversione ordini rate complete",
    installments_abandon_rate: "Tasso abbandono rate",
    installments_expired_unpaid_rate: "Tasso scadenza rate",
    installments_recovery_rate: "Tasso recupero rate",
    installments_avg_payment_time_days: "Tempo medio pagamento rate",
    installments_avg_recovery_time_days: "Tempo medio recupero rate",
};

const CARD_INFO_TEXT = {
    unique_customers: "Numero di clienti distinti che hanno creato almeno un ordine nel periodo.",
    total_orders: "Totale ordini creati nel periodo, indipendentemente dallo stato finale.",
    total_revenue: "Ricavi netti completati nel periodo: ordini pagati/rimborsati + rate pagate - rimborsi, filtrati per data di completamento.",
    succeeded_orders: "Ordini con pagamento completato con successo nel periodo, filtrati per data di completamento.",
    partially_paid_orders: "Ordini con pagamento a rate iniziato ma non ancora completato.",
    pending_orders: "Ordini in attesa di completamento pagamento.",
    abandoned_orders: "Ordini abbandonati senza completamento pagamento.",
    refunded_orders: "Ordini con rimborso completato nel periodo, filtrati per data di completamento del rimborso.",
    aov: "Average Order Value: ricavo medio per ordine completato nel periodo.",
    pending_to_succeeded_conversion_rate: "Percentuale di ordini convertiti: (succeeded + parzialmente pagati) su (succeeded + parzialmente pagati + pending + abbandonati).",
    abandon_rate: "Percentuale ordini abbandonati sul totale ordini del periodo.",
    refund_rate: "Percentuale ordini rimborsati sul totale ordini completati.",
    installment_orders: "Numero di ordini che contengono almeno una rata.",
    installment_adoption_rate: "Percentuale ordini con rate sul totale ordini del periodo.",
    installments_total_revenue: "Totale importi incassati dalle rate pagate nel periodo.",
    installments_pending_revenue: "Totale importi delle rate ancora in attesa di pagamento.",
    installments_abandoned_revenue: "Totale importi associati a rate abbandonate.",
    installments_conversion_rate: "Conversione ordini prima rata: ordini a rate con almeno una rata pagata su ordini a rate convertiti + pending + abbandonati.",
    installments_full_conversion_rate: "Conversione ordini a rate complete: ordini a rate con tutte le rate pagate sul totale ordini a rate.",
    installments_abandon_rate: "Percentuale ordini a rate che hanno almeno una rata abbandonata.",
    installments_expired_unpaid_rate: "Percentuale ordini a rate con almeno una rata scaduta e non pagata.",
    installments_recovery_rate: "Percentuale rate pagate dopo la scadenza sul totale delle rate.",
    installments_avg_payment_time_days: "Scostamento medio del pagamento rispetto alla scadenza: prima o dopo, espresso in giorni e ore.",
    installments_avg_recovery_time_days: "Tempo medio di recupero: quanto tempo passa tra scadenza e pagamento, considerando solo rate pagate dopo scadenza.",
    courses_table: "Breakdown per corso con metriche ordini, revenue e tassi per identificare i corsi con performance migliori o critiche.",
    course_orders: "Elenco ordini del corso selezionato con stato, importi e rimborsi per analisi di dettaglio.",
};

const OrdersReports = () => {
    const [loadingOverview, setLoadingOverview] = useState(false);
    const [overviewData, setOverviewData] = useState(null);
    const [courseSearch, setCourseSearch] = useState("");
    const [courseType, setCourseType] = useState(null);
    const [courseCategory, setCourseCategory] = useState(null);
    const [courseDialogOpen, setCourseDialogOpen] = useState(false);
    const [courseDialogLoading, setCourseDialogLoading] = useState(false);
    const [courseDialogData, setCourseDialogData] = useState(null);
    const [filters, setFilters] = useState(() => {
        const minDate = getMinOrdersAnalyticsDate();
        const thirtyDaysAgo = addDays(new Date(), -30);
        thirtyDaysAgo.setHours(0, 0, 0, 0);

        const fromDateTimestamp = Math.max(thirtyDaysAgo.getTime(), minDate.getTime());
        const initialFromDate = new Date(fromDateTimestamp);

        const initialToDate = (() => {
            const now = new Date();
            now.setHours(23, 59, 59, 999);
            return now;
        })();

        return {
            from_date: initialFromDate,
            to_date: initialToDate,
        };
    });

    const datePickerOptions = useMemo(() => ({
        css: [
            "https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css",
            "https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css",
            "/css/easepick-range.css",
        ],
        zIndex: 1000,
        plugins: [
            RangePlugin,
            PresetPlugin,
            AmpPlugin,
            LockPlugin,
        ],
        lang: "it-IT",
        format: "DD MMM YYYY",
        LockPlugin: {
            minDate: getMinOrdersAnalyticsDate(),
        },
        AmpPlugin: {
            darkMode: false,
            dropdown: {
                months: true,
                years: true,
                minYear: 2020,
            },
        },
        PresetPlugin: {
            customPreset: {
                "Oggi": [
                    (() => {
                        const date = new Date();
                        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                    })(),
                    (() => {
                        const date = new Date();
                        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                    })(),
                ],
                "7 giorni": [
                    (() => {
                        const date = new Date();
                        date.setDate(date.getDate() - 6);
                        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                    })(),
                    (() => {
                        const date = new Date();
                        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                    })(),
                ],
                "30 giorni": [
                    (() => {
                        const date = new Date();
                        date.setDate(date.getDate() - 29);
                        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                    })(),
                    (() => {
                        const date = new Date();
                        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                    })(),
                ],
                "Mese": [
                    (() => {
                        const now = new Date();
                        return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
                    })(),
                    (() => {
                        const now = new Date();
                        return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
                    })(),
                ],
            },
        },
        setup(picker) {
            picker.on("select", (event) => {
                const { start, end } = event.detail;
                const startDate = new Date(start.getTime());
                startDate.setHours(0, 0, 0, 0);
                const endDate = new Date(end.getTime());
                endDate.setHours(23, 59, 59, 999);

                setFilters((prev) => ({
                    ...prev,
                    from_date: startDate,
                    to_date: endDate,
                }));
            });
        },
    }), []);

    const getOrdersOverview = useCallback(async () => {
        setLoadingOverview(true);
        try {
            const params = new URLSearchParams();
            const fromDateRome = formatDateAsRomeDay(filters.from_date);
            const toDateRome = formatDateAsRomeDay(filters.to_date);

            if (fromDateRome) {
                params.append("start_date", fromDateRome);
            }
            if (toDateRome) {
                params.append("end_date", toDateRome);
            }

            const query = params.toString();
            const response = await api.get(`/admin/reports/orders/overview${query ? `?${query}` : ""}`);
            setOverviewData(response);
        } catch (error) {
            console.error(error);
        }
        setLoadingOverview(false);
    }, [filters.from_date, filters.to_date]);

    useEffect(() => {
        getOrdersOverview();
    }, [getOrdersOverview]);

    const actual = useMemo(() => overviewData?.actual || {}, [overviewData]);
    const previous = useMemo(() => overviewData?.previous || {}, [overviewData]);
    const actualInstallments = useMemo(() => overviewData?.actual_installments || {}, [overviewData]);
    const previousInstallments = useMemo(() => overviewData?.previous_installments || {}, [overviewData]);
    const actualDaily = useMemo(() => overviewData?.actual_daily || [], [overviewData]);
    const previousDaily = useMemo(() => overviewData?.previous_daily || [], [overviewData]);
    const actualByCourse = useMemo(() => overviewData?.actual_by_course || [], [overviewData]);
    const actualPaymentMethods = useMemo(() => overviewData?.actual_payment_methods || [], [overviewData]);

    const metricTrendData = useMemo(() => {
        const mapActualSeries = (series, key) => (series || []).map((item) => ({
            date: item?.date,
            value: Number(item?.[key] ?? 0),
        }));

        const mapPreviousSeriesAligned = (series, actualSeries, key) => (series || []).map((item, index) => ({
            date: actualSeries?.[index]?.date || item?.date,
            previous_date: item?.date,
            value: Number(item?.[key] ?? 0),
        }));

        const buildTrend = (key) => {
            const actualSeries = mapActualSeries(actualDaily, key);
            return {
                actual: actualSeries,
                previous: mapPreviousSeriesAligned(previousDaily, actualSeries, key),
            };
        };

        return {
            unique_customers: buildTrend("unique_customers"),
            total_orders: buildTrend("total_orders"),
            total_revenue: buildTrend("total_revenue"),
            succeeded_orders: buildTrend("succeeded_orders"),
            aov: buildTrend("aov"),
            pending_to_succeeded_conversion_rate: buildTrend("pending_to_succeeded_conversion_rate"),
            abandon_rate: buildTrend("abandon_rate"),
            refund_rate: buildTrend("refund_rate"),
        };
    }, [actualDaily, previousDaily]);

    const renderTrendTooltip = useCallback((metricKey, metricTitle) => {
        return ({ active, payload }) => {
            if (!active || !payload || payload.length === 0) {
                return null;
            }

            const actualPoint = payload.find((entry) => entry?.name === "Attuale") || payload[0];
            const previousPoint = payload.find((entry) => entry?.name === "Precedente") || payload[1];

            const formatValue = (rawValue) => {
                if (["total_revenue", "aov"].includes(metricKey)) {
                    return formatCurrencyFromCents(rawValue);
                }
                if (["pending_to_succeeded_conversion_rate", "abandon_rate", "refund_rate"].includes(metricKey)) {
                    return formatPercent(rawValue);
                }
                return formatInteger(rawValue);
            };

            const normalizedTitle = TOOLTIP_METRIC_LABELS[metricKey] || metricTitle;

            return (
                <div style={{ backgroundColor: "#fff", padding: "10px", borderRadius: "8px", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>
                    <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "0.35rem", color: "rgba(var(--text-color-rgb), 72%)", textTransform: "none", letterSpacing: "normal" }}>{normalizedTitle}</div>
                    {actualPoint && (
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", fontSize: "11px", color: "var(--primary)", marginBottom: "0.2rem" }}>
                            <span>{formatLongDay(actualPoint?.payload?.date)}</span>
                            <span>{formatValue(actualPoint?.value)}</span>
                        </div>
                    )}
                    {previousPoint && (
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", fontSize: "11px", color: "rgba(var(--text-color-rgb), .6)" }}>
                            <span>{formatLongDay(previousPoint?.payload?.previous_date || previousPoint?.payload?.date)}</span>
                            <span>{formatValue(previousPoint?.value)}</span>
                        </div>
                    )}
                </div>
            );
        };
    }, []);

    const renderCardInfoTag = useCallback((text) => {
        if (!text) {
            return null;
        }

        return (
            <TooltipWrapper text={text} className={styles.cardInfoTooltip}>
                <button
                    type="button"
                    className={styles.cardInfoButton}
                    onClick={(event) => event.stopPropagation()}
                    aria-label="Informazioni metrica"
                >
                    <InfoIcon className={styles.cardInfoIcon} />
                </button>
            </TooltipWrapper>
        );
    }, []);

    const getTrendYAxisDomain = useCallback((trend) => {
        const values = [
            ...(trend?.actual || []),
            ...(trend?.previous || []),
        ]
            .map((item) => Number(item?.value ?? 0))
            .filter((value) => Number.isFinite(value));

        if (!values.length) {
            return ["auto", "auto"];
        }

        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        const spread = maxValue - minValue;
        const basePadding = spread > 0 ? spread * 0.16 : Math.max(Math.abs(maxValue) * 0.16, 1);

        const lowerPadding = basePadding * 0.25;
        const upperPadding = basePadding;

        return [
            minValue - lowerPadding,
            maxValue + upperPadding,
        ];
    }, []);

    const volumeMetrics = useMemo(() => {
        return [
            {
                key: "unique_customers",
                title: "CLIENTI CREATI",
                actualValue: formatInteger(actual.unique_customers),
                previousValue: formatInteger(previous.unique_customers),
                percentageValue: calculatePercentageDifference(actual.unique_customers, previous.unique_customers),
                isLoss: calculatePercentageDifference(actual.unique_customers, previous.unique_customers) < 0,
                trend: metricTrendData.unique_customers,
            },
            {
                key: "total_orders",
                title: "ORDINI CREATI",
                actualValue: formatInteger(actual.total_orders),
                previousValue: formatInteger(previous.total_orders),
                percentageValue: calculatePercentageDifference(actual.total_orders, previous.total_orders),
                isLoss: calculatePercentageDifference(actual.total_orders, previous.total_orders) < 0,
                trend: metricTrendData.total_orders,
            },
            {
                key: "total_revenue",
                title: "INCASSI COMPLETATI",
                actualValue: formatCurrencyFromCents(actual.total_revenue),
                previousValue: formatCurrencyFromCents(previous.total_revenue),
                percentageValue: calculatePercentageDifference(actual.total_revenue, previous.total_revenue),
                isLoss: calculatePercentageDifference(actual.total_revenue, previous.total_revenue) < 0,
                trend: metricTrendData.total_revenue,
            },
            {
                key: "succeeded_orders",
                title: "ORDINI COMPLETATI",
                actualValue: formatInteger(actual.succeeded_orders),
                previousValue: formatInteger(previous.succeeded_orders),
                percentageValue: calculatePercentageDifference(actual.succeeded_orders, previous.succeeded_orders),
                isLoss: calculatePercentageDifference(actual.succeeded_orders, previous.succeeded_orders) < 0,
                trend: metricTrendData.succeeded_orders,
            },
            {
                key: "partially_paid_orders",
                title: "ORDINI PARZIALMENTE PAGATI",
                actualValue: formatInteger(actual.partially_paid_orders),
                previousValue: formatInteger(previous.partially_paid_orders),
                percentageValue: calculatePercentageDifference(actual.partially_paid_orders, previous.partially_paid_orders),
                isLoss: calculatePercentageDifference(actual.partially_paid_orders, previous.partially_paid_orders) < 0,
            },
            {
                key: "pending_orders",
                title: "ORDINI IN ATTESA",
                actualValue: formatInteger(actual.pending_orders),
                previousValue: formatInteger(previous.pending_orders),
                percentageValue: calculatePercentageDifference(actual.pending_orders, previous.pending_orders),
                isLoss: calculatePercentageDifference(actual.pending_orders, previous.pending_orders) > 0,
            },
            {
                key: "abandoned_orders",
                title: "ORDINI ABBANDONATI",
                actualValue: formatInteger(actual.abandoned_orders),
                previousValue: formatInteger(previous.abandoned_orders),
                percentageValue: calculatePercentageDifference(actual.abandoned_orders, previous.abandoned_orders),
                isLoss: calculatePercentageDifference(actual.abandoned_orders, previous.abandoned_orders) > 0,
            },
            {
                key: "refunded_orders",
                title: "RIMBORSI COMPLETATI",
                actualValue: formatInteger(actual.refunded_orders),
                previousValue: formatInteger(previous.refunded_orders),
                percentageValue: calculatePercentageDifference(actual.refunded_orders, previous.refunded_orders),
                isLoss: calculatePercentageDifference(actual.refunded_orders, previous.refunded_orders) > 0,
            },
        ];
    }, [actual, previous, metricTrendData]);

    const keyMetrics = useMemo(() => {
        return [
            {
                key: "aov",
                title: "AVERAGE ORDER VALUE (AOV)",
                actualValue: formatCurrencyFromCents(actual.aov),
                previousValue: formatCurrencyFromCents(previous.aov),
                percentageValue: calculatePercentageDifference(actual.aov, previous.aov),
                isLoss: calculatePercentageDifference(actual.aov, previous.aov) < 0,
                trend: metricTrendData.aov,
            },
            {
                key: "pending_to_succeeded_conversion_rate",
                title: "TASSO DI CONVERSIONE",
                actualValue: formatPercent(actual.pending_to_succeeded_conversion_rate),
                previousValue: formatPercent(previous.pending_to_succeeded_conversion_rate),
                percentageValue: calculatePercentageDifference(
                    actual.pending_to_succeeded_conversion_rate,
                    previous.pending_to_succeeded_conversion_rate,
                ),
                isLoss: calculatePercentageDifference(
                    actual.pending_to_succeeded_conversion_rate,
                    previous.pending_to_succeeded_conversion_rate,
                ) < 0,
                badgeDecimals: 2,
                trend: metricTrendData.pending_to_succeeded_conversion_rate,
            },
            {
                key: "abandon_rate",
                title: "TASSO DI ABBANDONO",
                actualValue: formatPercent(actual.abandon_rate),
                previousValue: formatPercent(previous.abandon_rate),
                percentageValue: calculatePercentageDifference(actual.abandon_rate, previous.abandon_rate),
                isLoss: calculatePercentageDifference(actual.abandon_rate, previous.abandon_rate) > 0,
                badgeDecimals: 2,
                trend: metricTrendData.abandon_rate,
            },
            {
                key: "refund_rate",
                title: "TASSO DI RIMBORSO",
                actualValue: formatPercent(actual.refund_rate),
                previousValue: formatPercent(previous.refund_rate),
                percentageValue: calculatePercentageDifference(actual.refund_rate, previous.refund_rate),
                isLoss: calculatePercentageDifference(actual.refund_rate, previous.refund_rate) > 0,
                badgeDecimals: 2,
                trend: metricTrendData.refund_rate,
            },
        ];
    }, [actual, previous, metricTrendData]);

    const installmentsMetrics = useMemo(() => {
        return [
            {
                key: "installment_orders",
                title: "NUMERO ORDINI A RATE",
                actualValue: formatInteger(actualInstallments.installment_orders),
                previousValue: formatInteger(previousInstallments.installment_orders),
                percentageValue: calculatePercentageDifference(actualInstallments.installment_orders, previousInstallments.installment_orders),
                isLoss: calculatePercentageDifference(actualInstallments.installment_orders, previousInstallments.installment_orders) < 0,
            },
            {
                key: "installment_adoption_rate",
                title: "TASSO ADOZIONE RATE",
                actualValue: formatPercent(actualInstallments.installment_adoption_rate),
                previousValue: formatPercent(previousInstallments.installment_adoption_rate),
                percentageValue: calculatePercentageDifference(actualInstallments.installment_adoption_rate, previousInstallments.installment_adoption_rate),
                isLoss: calculatePercentageDifference(actualInstallments.installment_adoption_rate, previousInstallments.installment_adoption_rate) < 0,
                badgeDecimals: 2,
            },
            {
                key: "installments_total_revenue",
                title: "TOTAL REVENUES RATE",
                actualValue: formatCurrencyFromCents(actualInstallments.installments_total_revenue),
                previousValue: formatCurrencyFromCents(previousInstallments.installments_total_revenue),
                percentageValue: calculatePercentageDifference(actualInstallments.installments_total_revenue, previousInstallments.installments_total_revenue),
                isLoss: calculatePercentageDifference(actualInstallments.installments_total_revenue, previousInstallments.installments_total_revenue) < 0,
            },
            {
                key: "installments_pending_revenue",
                title: "REVENUES IN ATTESA RATE",
                actualValue: formatCurrencyFromCents(actualInstallments.installments_pending_revenue),
                previousValue: formatCurrencyFromCents(previousInstallments.installments_pending_revenue),
                percentageValue: calculatePercentageDifference(actualInstallments.installments_pending_revenue, previousInstallments.installments_pending_revenue),
                isLoss: calculatePercentageDifference(actualInstallments.installments_pending_revenue, previousInstallments.installments_pending_revenue) > 0,
            },
            {
                key: "installments_abandoned_revenue",
                title: "REVENUES RATE ABBANDONATE",
                actualValue: formatCurrencyFromCents(actualInstallments.installments_abandoned_revenue),
                previousValue: formatCurrencyFromCents(previousInstallments.installments_abandoned_revenue),
                percentageValue: calculatePercentageDifference(actualInstallments.installments_abandoned_revenue, previousInstallments.installments_abandoned_revenue),
                isLoss: calculatePercentageDifference(actualInstallments.installments_abandoned_revenue, previousInstallments.installments_abandoned_revenue) > 0,
            },
            {
                key: "installments_conversion_rate",
                title: "CONVERSIONE ORDINI PRIMA RATA",
                actualValue: formatPercent(actualInstallments.installments_conversion_rate),
                previousValue: formatPercent(previousInstallments.installments_conversion_rate),
                percentageValue: calculatePercentageDifference(actualInstallments.installments_conversion_rate, previousInstallments.installments_conversion_rate),
                isLoss: calculatePercentageDifference(actualInstallments.installments_conversion_rate, previousInstallments.installments_conversion_rate) < 0,
                badgeDecimals: 2,
            },
            {
                key: "installments_full_conversion_rate",
                title: "CONVERSIONE ORDINI RATE COMPLETE",
                actualValue: formatPercent(actualInstallments.installments_full_conversion_rate),
                previousValue: formatPercent(previousInstallments.installments_full_conversion_rate),
                percentageValue: calculatePercentageDifference(actualInstallments.installments_full_conversion_rate, previousInstallments.installments_full_conversion_rate),
                isLoss: calculatePercentageDifference(actualInstallments.installments_full_conversion_rate, previousInstallments.installments_full_conversion_rate) < 0,
                badgeDecimals: 2,
            },
            {
                key: "installments_abandon_rate",
                title: "TASSO ABBANDONO RATE",
                actualValue: formatPercent(actualInstallments.installments_abandon_rate),
                previousValue: formatPercent(previousInstallments.installments_abandon_rate),
                percentageValue: calculatePercentageDifference(actualInstallments.installments_abandon_rate, previousInstallments.installments_abandon_rate),
                isLoss: calculatePercentageDifference(actualInstallments.installments_abandon_rate, previousInstallments.installments_abandon_rate) > 0,
                badgeDecimals: 2,
            },
            {
                key: "installments_expired_unpaid_rate",
                title: "TASSO SCADENZA RATE",
                actualValue: formatPercent(actualInstallments.installments_expired_unpaid_rate),
                previousValue: formatPercent(previousInstallments.installments_expired_unpaid_rate),
                percentageValue: calculatePercentageDifference(actualInstallments.installments_expired_unpaid_rate, previousInstallments.installments_expired_unpaid_rate),
                isLoss: calculatePercentageDifference(actualInstallments.installments_expired_unpaid_rate, previousInstallments.installments_expired_unpaid_rate) > 0,
                badgeDecimals: 2,
            },
            {
                key: "installments_recovery_rate",
                title: "TASSO RECUPERO RATE",
                actualValue: formatPercent(actualInstallments.installments_recovery_rate),
                previousValue: formatPercent(previousInstallments.installments_recovery_rate),
                percentageValue: calculatePercentageDifference(actualInstallments.installments_recovery_rate, previousInstallments.installments_recovery_rate),
                isLoss: calculatePercentageDifference(actualInstallments.installments_recovery_rate, previousInstallments.installments_recovery_rate) < 0,
                badgeDecimals: 2,
            },
            {
                key: "installments_avg_recovery_time_days",
                title: "TEMPO RECUPERO RATE",
                actualValue: formatSignedDaysHours(actualInstallments.installments_avg_recovery_time_days),
                previousValue: formatSignedDaysHours(previousInstallments.installments_avg_recovery_time_days),
                percentageValue: calculatePercentageDifference(actualInstallments.installments_avg_recovery_time_days, previousInstallments.installments_avg_recovery_time_days),
                isLoss: calculatePercentageDifference(actualInstallments.installments_avg_recovery_time_days, previousInstallments.installments_avg_recovery_time_days) > 0,
                badgeDecimals: 2,
            },
            {
                key: "installments_avg_payment_time_days",
                title: "TEMPO MEDIO PAGAMENTO RATE",
                actualValue: formatSignedDaysHours(actualInstallments.installments_avg_payment_time_days),
                previousValue: formatSignedDaysHours(previousInstallments.installments_avg_payment_time_days),
                percentageValue: calculatePercentageDifference(actualInstallments.installments_avg_payment_time_days, previousInstallments.installments_avg_payment_time_days),
                isLoss: calculatePercentageDifference(actualInstallments.installments_avg_payment_time_days, previousInstallments.installments_avg_payment_time_days) > 0,
                badgeDecimals: 2,
            },
        ];
    }, [actualInstallments, previousInstallments]);

    const courseBreakdownColumns = useMemo(() => ([
        { key: "course_name", label: "CORSO", type: "string", wrap: true, style: { minWidth: "260px" } },
        { key: "unique_customers", label: "CLIENTI CREATI", type: "number" },
        { key: "completed_unique_customers", label: "CLIENTI COMPLETATI", type: "number" },
        { key: "total_orders", label: "ORDINI CREATI", type: "number" },
        { key: "total_revenue", label: "INCASSI COMPLETATI", type: "custom" },
        { key: "succeeded_orders", label: "ORDINI COMPLETATI", type: "number" },
        { key: "partially_paid_orders", label: "PARZ. PAGATI", type: "number" },
        { key: "pending_orders", label: "IN ATTESA", type: "number" },
        { key: "abandoned_orders", label: "ABBANDONATI", type: "number" },
        { key: "refunded_orders", label: "RIMBORSATI", type: "number" },
        { key: "conversion_rate", label: "TASSO DI CONVERSIONE", type: "custom" },
        { key: "abandon_rate", label: "TASSO DI ABBANDONO", type: "custom" },
    ]), []);

    const paymentMethodsColumns = useMemo(() => ([
        { key: "payment_method", label: "METODO", type: "string" },
        { key: "total_revenue", label: "INCASSI COMPLETATI", type: "custom" },
        { key: "revenue_volume_percentage", label: "VOLUME %", type: "custom" },
        { key: "orders_count", label: "ORDINI COMPLETATI", type: "number" },
        { key: "installments_count", label: "RATE", type: "number" },
    ]), []);

    const getCourseReport = useCallback(async (courseId) => {
        if (!courseId) return;

        setCourseDialogOpen(true);
        setCourseDialogLoading(true);
        try {
            const params = new URLSearchParams();
            const fromDateRome = formatDateAsRomeDay(filters.from_date);
            const toDateRome = formatDateAsRomeDay(filters.to_date);

            if (fromDateRome) {
                params.append("start_date", fromDateRome);
            }
            if (toDateRome) {
                params.append("end_date", toDateRome);
            }

            const query = params.toString();
            const response = await api.get(`/admin/reports/orders/course/${courseId}${query ? `?${query}` : ""}`);
            setCourseDialogData(response);
        } catch (error) {
            console.error(error);
            setCourseDialogData(null);
        }
        setCourseDialogLoading(false);
    }, [filters.from_date, filters.to_date]);

    const filteredCourseBreakdown = useMemo(() => {
        const normalizedSearch = (courseSearch || "").trim().toLowerCase();
        const selectedType = courseType?.value;
        const selectedCategory = courseCategory?.value;

        return (actualByCourse || []).filter((row) => {
            if (selectedType && String(row?.course_type || "") !== String(selectedType)) {
                return false;
            }

            if (selectedCategory && String(row?.course_category || "") !== String(selectedCategory)) {
                return false;
            }

            if (!normalizedSearch) {
                return true;
            }

            return String(row?.course_name || "").toLowerCase().includes(normalizedSearch);
        });
    }, [actualByCourse, courseSearch, courseType, courseCategory]);

    const courseBreakdownElements = useMemo(() => {
        const mappedRows = (filteredCourseBreakdown || []).map((row) => ({
            id: row.course_id,
            course_name: row.course_name,
            course_type: row.course_type,
            course_category: row.course_category,
            unique_customers: Number(row.unique_customers ?? 0),
            completed_unique_customers: Number(row.completed_unique_customers ?? 0),
            total_orders: Number(row.total_orders ?? 0),
            created_succeeded_orders: Number(row.created_succeeded_orders ?? row.succeeded_orders ?? 0),
            created_partially_paid_orders: Number(row.created_partially_paid_orders ?? row.partially_paid_orders ?? 0),
            total_revenue: {
                value: Number(row.total_revenue ?? 0),
                type: "number",
                component: <div>{formatCurrencyFromCents(row.total_revenue)}</div>,
            },
            succeeded_orders: Number(row.succeeded_orders ?? 0),
            partially_paid_orders: Number(row.partially_paid_orders ?? 0),
            pending_orders: Number(row.pending_orders ?? 0),
            abandoned_orders: Number(row.abandoned_orders ?? 0),
            refunded_orders: Number(row.refunded_orders ?? 0),
            conversion_rate: {
                value: Number(row.conversion_rate ?? 0),
                type: "number",
                component: <div>{formatPercent(row.conversion_rate)}</div>,
            },
            abandon_rate: {
                value: Number(row.abandon_rate ?? 0),
                type: "number",
                component: <div>{formatPercent(row.abandon_rate)}</div>,
            },
        }));

        const totals = mappedRows.reduce((acc, row) => {
            acc.unique_customers += Number(row.unique_customers ?? 0);
            acc.completed_unique_customers += Number(row.completed_unique_customers ?? 0);
            acc.total_orders += Number(row.total_orders ?? 0);
            acc.created_succeeded_orders += Number(row.created_succeeded_orders ?? 0);
            acc.created_partially_paid_orders += Number(row.created_partially_paid_orders ?? 0);
            acc.total_revenue += Number(row.total_revenue?.value ?? 0);
            acc.succeeded_orders += Number(row.succeeded_orders ?? 0);
            acc.partially_paid_orders += Number(row.partially_paid_orders ?? 0);
            acc.pending_orders += Number(row.pending_orders ?? 0);
            acc.abandoned_orders += Number(row.abandoned_orders ?? 0);
            acc.refunded_orders += Number(row.refunded_orders ?? 0);
            return acc;
        }, {
            unique_customers: 0,
            completed_unique_customers: 0,
            total_orders: 0,
            created_succeeded_orders: 0,
            created_partially_paid_orders: 0,
            total_revenue: 0,
            succeeded_orders: 0,
            partially_paid_orders: 0,
            pending_orders: 0,
            abandoned_orders: 0,
            refunded_orders: 0,
        });

        const totalConversionRate = totals.created_succeeded_orders + totals.created_partially_paid_orders + totals.pending_orders + totals.abandoned_orders > 0
            ? ((totals.created_succeeded_orders + totals.created_partially_paid_orders) / (totals.created_succeeded_orders + totals.created_partially_paid_orders + totals.pending_orders + totals.abandoned_orders)) * 100
            : 0;
        const totalAbandonRate = totals.total_orders > 0
            ? (totals.abandoned_orders / totals.total_orders) * 100
            : 0;

        const totalsRow = {
            id: "total-row",
            course_name: "TOTALE",
            unique_customers: totals.unique_customers,
            completed_unique_customers: totals.completed_unique_customers,
            total_orders: totals.total_orders,
            created_succeeded_orders: totals.created_succeeded_orders,
            created_partially_paid_orders: totals.created_partially_paid_orders,
            total_revenue: {
                value: totals.total_revenue,
                type: "number",
                component: <div>{formatCurrencyFromCents(totals.total_revenue)}</div>,
            },
            succeeded_orders: totals.succeeded_orders,
            partially_paid_orders: totals.partially_paid_orders,
            pending_orders: totals.pending_orders,
            abandoned_orders: totals.abandoned_orders,
            refunded_orders: totals.refunded_orders,
            conversion_rate: {
                value: totalConversionRate,
                type: "number",
                component: <div>{formatPercent(totalConversionRate)}</div>,
            },
            abandon_rate: {
                value: totalAbandonRate,
                type: "number",
                component: <div>{formatPercent(totalAbandonRate)}</div>,
            },
        };

        return [totalsRow, ...mappedRows];
    }, [filteredCourseBreakdown]);

    const paymentMethodsElements = useMemo(() => {
        return (actualPaymentMethods || []).map((row, index) => ({
            id: `${row.payment_method || "unknown"}-${index}`,
            payment_method: formatPaymentMethodLabel(row.payment_method),
            total_revenue: {
                value: Number(row.total_revenue ?? 0),
                type: "number",
                component: <div>{formatCurrencyFromCents(row.total_revenue)}</div>,
            },
            revenue_volume_percentage: {
                value: Number(row.revenue_volume_percentage ?? 0),
                type: "number",
                component: <div>{formatPercent(row.revenue_volume_percentage)}</div>,
            },
            orders_count: Number(row.orders_count ?? 0),
            installments_count: Number(row.installments_count ?? 0),
        }));
    }, [actualPaymentMethods]);

    const courseDialogMetrics = useMemo(() => {
        const metrics = courseDialogData?.metrics || {};
        return [
            { key: "unique_customers", label: "Clienti unici", value: formatInteger(metrics.unique_customers) },
            { key: "total_orders", label: "Ordini", value: formatInteger(metrics.total_orders) },
            { key: "total_revenue", label: "Total revenue", value: formatCurrencyFromCents(metrics.total_revenue) },
            { key: "succeeded_orders", label: "Ordini completati", value: formatInteger(metrics.succeeded_orders) },
            { key: "partially_paid_orders", label: "Parz. pagati", value: formatInteger(metrics.partially_paid_orders) },
            { key: "pending_orders", label: "In attesa", value: formatInteger(metrics.pending_orders) },
            { key: "abandoned_orders", label: "Abbandonati", value: formatInteger(metrics.abandoned_orders) },
            { key: "refunded_orders", label: "Rimborsati", value: formatInteger(metrics.refunded_orders) },
            { key: "conversion_rate", label: "Tasso di conversione", value: formatPercent(metrics.pending_to_succeeded_conversion_rate) },
            { key: "abandon_rate", label: "Tasso di abbandono", value: formatPercent(metrics.abandon_rate) },
            { key: "refund_rate", label: "Tasso di rimborso", value: formatPercent(metrics.refund_rate) },
        ];
    }, [courseDialogData]);

    const courseOrdersColumns = useMemo(() => ([
        { key: "order_no", label: "CODICE", type: "string" },
        { key: "created_at", label: "CREATO IL", type: "datetime" },
        { key: "completed_at", label: "COMPLETATO IL", type: "datetime" },
        { key: "user", label: "UTENTE", type: "string" },
        { key: "products", label: "PRODOTTI CORSO", type: "custom", wrap: true },
        { key: "amount", label: "IMPORTO", type: "custom" },
        { key: "refunded_amount", label: "RIMBORSO", type: "custom" },
        { key: "status", label: "STATO", type: "custom" },
    ]), []);

    const courseOrdersElements = useMemo(() => {
        return (courseDialogData?.orders || []).map((order) => ({
            id: order.id,
            href: `/orders/${order.id}`,
            order_no: order.order_no,
            created_at: order.created_at,
            completed_at: order.completed_at,
            user: `${order?.user?.name || ""} ${order?.user?.surname || ""}`.trim() || order?.user?.email || "N/A",
            products: {
                value: (order.products || []).join(", "),
                type: "string",
                component: (
                    <div style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                    }}>
                        {(order.products || []).join(", ") || "N/A"}
                    </div>
                ),
            },
            amount: {
                value: Number(order.amount ?? 0),
                type: "number",
                component: <div>{formatCurrencyFromCents(order.amount)}</div>,
            },
            refunded_amount: {
                value: Number(order.refunded_amount ?? 0),
                type: "number",
                component: <div>{formatCurrencyFromCents(order.refunded_amount)}</div>,
            },
            status: {
                value: order.status,
                type: "string",
                component: (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", flexWrap: "wrap" }}>
                        {order.test && <StatusBadge appearance="soft" label="Test" color="var(--sf-orange)" />}
                        {order.status && <StatusBadge appearance="soft" {...getOrderStatusBadge(order.status)} />}
                    </div>
                ),
            },
        }));
    }, [courseDialogData]);

    return (
        <HeaderFooterLayout>
            <HelmetProvider><Helmet><title>Report - Ordini</title></Helmet></HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <Breadcrumb items={[{ label: "Report" }, { label: "Ordini" }]} />
                        <header className={styles.header}><div><h1>Report ordini</h1><p>Analizza vendite, incassi e pagamenti nel periodo selezionato.</p></div></header>

                        <div className={styles.filters}>
                            <label className={styles.dateField}><span>Periodo di analisi</span><EasePicker
                                options={datePickerOptions}
                                placeholder="Seleziona Date"
                                startDate={filters.from_date ? new Date(filters.from_date) : undefined}
                                endDate={filters.to_date ? new Date(filters.to_date) : undefined}
                                className={styles.datePicker}
                            /></label>
                            <nav className={styles.sectionNav} aria-label="Sezioni del report">
                                <a href="#sales-overview">Panoramica</a><a href="#sales-courses">Corsi</a><a href="#sales-payments">Pagamenti</a><a href="#sales-installments">Rate</a>
                            </nav>
                        </div>

                        <div className={styles.reportSectionHeader}>
                            <h2 id="sales-overview" className={styles.reportSectionTitle}>Panoramica</h2>
                            <div className={styles.reportSectionObjective}>Panoramica generale del periodo selezionato.</div>
                        </div>

                        <div className={styles.metricHeading}><h3 className={styles.analyticsTitle}>Volume vendite</h3><div className={styles.legend}><span>Periodo selezionato</span><span>Periodo precedente</span></div></div>
                        <div className={styles.analyticsGrid}>
                            {volumeMetrics.map((metric) => (
                                <Card appearance="modern" key={metric.key} title={metric.title} actions={renderCardInfoTag(CARD_INFO_TEXT[metric.key])}>
                                    <MetricStatBlock
                                        title=""
                                        actualValue={metric.actualValue}
                                        previousValue={metric.previousValue}
                                        percentageValue={metric.percentageValue}
                                        isLoss={metric.isLoss}
                                        loading={loadingOverview}
                                        containerClassName={styles.analytic}
                                        titleClassName={styles.analyticTitle}
                                        actualClassName={styles.actualAnalytic}
                                        previousClassName={styles.previousAnalytic}
                                        skeletonClassName={styles.analyticSkeleton}
                                    />
                                    {!!metric?.trend?.actual?.length && (
                                        <div className={styles.trendChartContainer}>
                                            <ResponsiveContainer width="100%" height={100}>
                                                <ComposedChart data={metric.trend.actual} margin={{ top: 14, right: 0, left: -18, bottom: 0 }}>
                                                    <CartesianGrid strokeWidth={1} stroke="#f3f3f3" horizontal vertical={false} />
                                                    <defs>
                                                        <linearGradient id={`ordersTrendGradient-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
                                                            <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <XAxis
                                                        dataKey="date"
                                                        tickFormatter={(value) => formatLongDay(value)}
                                                        tick={{ fill: "rgba(var(--text-color-rgb), 44%)", fontSize: "10px", strokeWidth: 0 }}
                                                    />
                                                    <YAxis hide domain={getTrendYAxisDomain(metric.trend)} />
                                                    <Tooltip
                                                        content={renderTrendTooltip(metric.key, metric.title)}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="value"
                                                        name="Attuale"
                                                        stroke="var(--primary)"
                                                        fill={`url(#ordersTrendGradient-${metric.key})`}
                                                        strokeWidth={2}
                                                        activeDot={{ r: 3 }}
                                                        dot={false}
                                                    />
                                                    {!!metric?.trend?.previous?.length && (
                                                        <Line
                                                            type="monotone"
                                                            data={metric.trend.previous}
                                                            dataKey="value"
                                                            name="Precedente"
                                                            stroke="rgba(var(--text-color-rgb), .35)"
                                                            strokeDasharray="4 3"
                                                            strokeWidth={2}
                                                            activeDot={{ r: 3 }}
                                                            dot={false}
                                                        />
                                                    )}
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </Card>
                            ))}
                        </div>

                        <h3 className={styles.analyticsTitle}>Metriche chiave</h3>
                        <div className={styles.analyticsGrid}>
                            {keyMetrics.map((metric) => (
                                <Card appearance="modern" key={metric.key} title={metric.title} actions={renderCardInfoTag(CARD_INFO_TEXT[metric.key])}>
                                    <MetricStatBlock
                                        title=""
                                        actualValue={metric.actualValue}
                                        previousValue={metric.previousValue}
                                        percentageValue={metric.percentageValue}
                                        isLoss={metric.isLoss}
                                        loading={loadingOverview}
                                        badgeDecimals={metric.badgeDecimals || 0}
                                        containerClassName={styles.analytic}
                                        titleClassName={styles.analyticTitle}
                                        actualClassName={styles.actualAnalytic}
                                        previousClassName={styles.previousAnalytic}
                                        skeletonClassName={styles.analyticSkeleton}
                                    />
                                    {!!metric?.trend?.actual?.length && (
                                        <div className={styles.trendChartContainer}>
                                            <ResponsiveContainer width="100%" height={100}>
                                                <ComposedChart data={metric.trend.actual} margin={{ top: 14, right: 0, left: -18, bottom: 0 }}>
                                                    <CartesianGrid strokeWidth={1} stroke="#f3f3f3" horizontal vertical={false} />
                                                    <defs>
                                                        <linearGradient id={`ordersTrendGradient-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
                                                            <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <XAxis
                                                        dataKey="date"
                                                        tickFormatter={(value) => formatLongDay(value)}
                                                        tick={{ fill: "rgba(var(--text-color-rgb), 44%)", fontSize: "10px", strokeWidth: 0 }}
                                                    />
                                                    <YAxis hide domain={getTrendYAxisDomain(metric.trend)} />
                                                    <Tooltip
                                                        content={renderTrendTooltip(metric.key, metric.title)}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="value"
                                                        name="Attuale"
                                                        stroke="var(--primary)"
                                                        fill={`url(#ordersTrendGradient-${metric.key})`}
                                                        strokeWidth={2}
                                                        activeDot={{ r: 3 }}
                                                        dot={false}
                                                    />
                                                    {!!metric?.trend?.previous?.length && (
                                                        <Line
                                                            type="monotone"
                                                            data={metric.trend.previous}
                                                            dataKey="value"
                                                            name="Precedente"
                                                            stroke="rgba(var(--text-color-rgb), .35)"
                                                            strokeDasharray="4 3"
                                                            strokeWidth={2}
                                                            activeDot={{ r: 3 }}
                                                            dot={false}
                                                        />
                                                    )}
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </Card>
                            ))}
                        </div>

                        <div className={styles.reportSectionHeader}><h2 id="sales-courses" className={styles.reportSectionTitle}>Vendite per corso</h2><p className={styles.reportSectionObjective}>Seleziona un corso per consultare gli ordini e il dettaglio degli incassi.</p></div>
                        <Card appearance="modern" integratedTable title="Elenco corsi">
                            <div className={styles.tableToolbar}>
                                <div className={styles.tableFilters}>
                                    <TextInput appearance="modern"
                                        type="search"
                                        placeholder="Cerca corso..."
                                        value={courseSearch}
                                        onKeyUp={(value) => setCourseSearch(value ?? "")}
                                    />
                                    <Select appearance="modern" portal
                                        selected={courseType}
                                        options={CourseTypes}
                                        placeholder="Tutti i tipi"
                                        nullable
                                        nullLabel="Tutti i tipi"
                                        onSelect={(option) => setCourseType(option?.value ? option : null)}
                                        style={{ width: "100%" }}
                                    />
                                    <Select appearance="modern" portal
                                        selected={courseCategory}
                                        options={CourseCategories}
                                        placeholder="Tutte le categorie"
                                        nullable
                                        nullLabel="Tutte le categorie"
                                        onSelect={(option) => setCourseCategory(option?.value ? option : null)}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                            </div>
                            <Table appearance="minimal"
                                style={{ border: 0, marginTop: 0, borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '480px', overflowY: 'auto', overflowX: 'auto' }}
                                loading={loadingOverview}
                                loadingN={8}
                                columns={courseBreakdownColumns}
                                elements={courseBreakdownElements}
                                defaultSort={{ key: "__none__", direction: "desc" }}
                                onRowClick={(row) => {
                                    if (!row?.id || row.id === "total-row") {
                                        return;
                                    }
                                    getCourseReport(row.id);
                                }}
                            />
                        </Card>

                        <div className={styles.reportSectionHeader}>
                            <h2 id="sales-payments" className={styles.reportSectionTitle}>Metodi di pagamento</h2>
                            <div className={styles.reportSectionObjective}>Distribuzione revenue e volumi per metodo di pagamento.</div>
                        </div>
                        <Card appearance="modern" integratedTable title="Metodi di pagamento">
                            <Table appearance="minimal"
                                style={{ border: 0, marginTop: 0, borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '420px', overflowY: 'auto', overflowX: 'auto' }}
                                loading={loadingOverview}
                                loadingN={6}
                                columns={paymentMethodsColumns}
                                elements={paymentMethodsElements}
                                defaultSort={{ key: "total_revenue", direction: "desc" }}
                            />
                        </Card>

                        <div className={styles.reportSectionHeader}>
                            <h2 id="sales-installments" className={styles.reportSectionTitle}>Pagamenti a rate</h2>
                            <div className={styles.reportSectionObjective}>Performance e qualità degli ordini con pagamento a rate.</div>
                        </div>
                        <div className={styles.analyticsGrid}>
                            {installmentsMetrics.map((metric) => (
                                <Card appearance="modern" key={metric.key} title={metric.title} actions={renderCardInfoTag(CARD_INFO_TEXT[metric.key])}>
                                    <MetricStatBlock
                                        title=""
                                        actualValue={metric.actualValue}
                                        previousValue={metric.previousValue}
                                        percentageValue={metric.percentageValue}
                                        isLoss={metric.isLoss}
                                        loading={loadingOverview}
                                        badgeDecimals={metric.badgeDecimals || 0}
                                        containerClassName={styles.analytic}
                                        titleClassName={styles.analyticTitle}
                                        actualClassName={styles.actualAnalytic}
                                        previousClassName={styles.previousAnalytic}
                                        skeletonClassName={styles.analyticSkeleton}
                                    />
                                </Card>
                            ))}
                        </div>

                        <Dialog appearance="modern"
                            open={courseDialogOpen}
                            onClose={() => {
                                setCourseDialogOpen(false);
                                setCourseDialogData(null);
                            }}
                            title={`Dettaglio corso - ${courseDialogData?.course?.name || ""}`}
                            style={{ width: "90%", maxWidth: "1200px" }}
                            contentMaxHeight="80vh"
                            overflow="auto"
                        >
                            <div className={styles.courseDetail}>
                                <div className={styles.detailMetrics}>
                                    {courseDialogMetrics.map((item) => (
                                        <div key={item.key} className={styles.detailMetric}>
                                            <div style={{ fontSize: "0.72rem", color: "rgba(var(--text-color-rgb), 58%)", marginBottom: "0.25rem" }}>{item.label}</div>
                                            <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>{item.value}</div>
                                        </div>
                                    ))}
                                </div>

                                <Card appearance="modern" integratedTable title="Ordini del corso">
                                    <Table appearance="minimal"
                                        style={{ border: 0, marginTop: 0, borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '420px', overflowY: 'auto', overflowX: 'auto' }}
                                        loading={courseDialogLoading}
                                        loadingN={8}
                                        columns={courseOrdersColumns}
                                        elements={courseOrdersElements}
                                        defaultSort={{ key: "created_at", direction: "desc" }}
                                    />
                                </Card>
                            </div>
                        </Dialog>
                    </div>
                </div>
            </div>
        </HeaderFooterLayout>
    );
};

export default OrdersReports;
